import React, { Fragment, useCallback, useMemo } from "react";
import { Line } from "react-konva";
import { TCalibration } from "./Calibration";
import { TModel } from "./models";
import useProjection from "./useProjection";

type Props = {
  model: TModel;
  scale: number;
  calibration?: TCalibration;
  baseUrl?: string;
  resolution: [number, number];
};

const eucledianDistance = (
  a: [number, number, number],
  b: [number, number, number]
) => {
  return Math.sqrt(
    Math.pow(b[0] - a[0], 2) +
      Math.pow(b[1] - a[1], 2) +
      Math.pow(b[2] - a[2], 2)
  );
};

const pointOnLine = (
  a: [number, number, number],
  b: [number, number, number],
  segments: number,
  i: number
) => {
  return [
    a[1] + ((b[1] - a[1]) * i) / segments,
    a[0] + ((b[0] - a[0]) * i) / segments,
    a[2] + ((b[2] - a[2]) * i) / segments,
  ];
};

const newLine = (
  startPoint: [number, number, number],
  endPoint: [number, number, number]
) => {
  const distance = eucledianDistance(startPoint, endPoint);
  console.log(distance);
  const segments = Math.floor(distance / 0.5); //Segments of 50cm min
  const ret: Array<Array<number>> = [];
  for (let i = 0; i < segments; i++) {
    ret.push(pointOnLine(startPoint, endPoint, segments, i));
  }
  ret.push([endPoint[1], endPoint[0], endPoint[2]]);
  return ret;
};

const PitchProjection = ({
  model,
  scale,
  calibration,
  baseUrl,
  resolution,
}: Props) => {
  const linesWithPoints = useMemo(() => {
    const ret: Array<Array<Array<number>>> = [];
    model.lines.forEach((line) => {
      ret.push(newLine(model.worldCoords[line[0]], model.worldCoords[line[1]]));
    });
    for (let i = 0; i < model.shapes.length; i++) {
      for (let j = 0; j < model.shapes[i].length - 1; j++) {
        ret.push(
          newLine(
            model.worldCoords[model.shapes[i][j]],
            model.worldCoords[model.shapes[i][j + 1]]
          )
        );
      }
    }
    return ret;
  }, [model]);

  const template = useMemo(() => {
    return linesWithPoints.flatMap((l) => [...l]);
  }, [linesWithPoints]);

  const { data } = useProjection(template, calibration, baseUrl);

  const outOfBounds = useCallback(
    (point: [number, number]) => {
      return !(
        point[0] >= 0 &&
        point[0] <= resolution[0] &&
        point[1] >= 0 &&
        point[1] <= resolution[1]
      );
    },
    [resolution]
  );

  const lines = useMemo(() => {
    const ret: Array<Array<number>> = [];
    let index = 0;
    if (data) {
      linesWithPoints.forEach((line) => {
        const add = [];
        let stop = false;
        //line has at least 2 points
        for (let i = 0; i < line.length; i++) {
          if (outOfBounds(data[index])) {
            stop = true;
          } else {
            stop = false;
          }
          if (!stop) {
            add.push(data[index][0]);
            add.push(data[index][1]);
          }
          index++;
        }
        ret.push(add);
      });
    }
    return ret;
  }, [data, linesWithPoints, outOfBounds]);

  const allLines = lines.map((l, i) => (
    <Line
      key={i}
      points={l}
      stroke="blue"
      closed={false}
      dashEnabled={false}
      strokeWidth={Math.ceil(1 / scale)}
    />
  ));

  return <Fragment>{allLines}</Fragment>;
};

export default PitchProjection;
